import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

/**
 * ProjectBriefModal
 * -------------------------------------------------------------
 * A richer pre-quote intake form that opens from a button next to
 * the standard contact form. Auto-saves to localStorage as the
 * user types/uploads, so they can leave and come back without
 * losing progress.
 *
 * Usage in your Contact section:
 *
 *   import ProjectBriefModal, { ProjectBriefLauncher } from "./ProjectBriefModal";
 *
 *   <ProjectBriefLauncher />
 *
 * That's it — the launcher renders the invite text + button, and
 * owns the open/close state for the modal internally. If you'd
 * rather control state yourself, import ProjectBriefModal directly
 * and pass isOpen / onClose props.
 * -------------------------------------------------------------
 */

const STORAGE_KEY = "aranabros_project_brief_draft_v1";
const MAX_IMAGES = 8;
const MAX_IMAGE_MB = 8;

const PROJECT_TYPES = [
  "Kitchen Remodel",
  "Bathroom Remodel",
  "Whole Home Remodel",
  "Addition",
  "Patio",
  "Interior Reconfiguration",
  "Flooring",
  "Finish Upgrades",
  "Other",
];

const COMMUNICATION_METHODS = ["Phone", "Text", "Email"];

const IMPROVEMENT_GOALS = [
  "Better functionality",
  "Updated appearance",
  "Increased home value",
  "More space",
  "Improved layout",
  "Aging in place / accessibility",
  "Energy efficiency",
  "Other",
];

const SCOPE_OPTIONS = [
  "Cosmetic upgrades only",
  "Layout changes",
  "Structural modifications",
  "Plumbing relocation",
  "Electrical upgrades",
  "Permit-required work",
];

const PLANNING_RESOURCES = [
  "Architectural plans",
  "Engineering plans",
  "Interior designer",
  "Inspiration photos",
  "Material selections already chosen",
];

const FINISH_LEVELS = [
  "Builder Grade",
  "Mid-Range",
  "High-End / Custom",
  "Unsure",
];

const CONTRACTOR_PRIORITIES = [
  "Quality craftsmanship",
  "Communication",
  "Budget",
  "Timeline",
  "Design guidance",
  "Experience",
  "Other",
];

const SITE_CONCERNS = [
  "Limited parking",
  "Restricted work hours",
  "Existing damage / issues",
  "Older plumbing / electrical",
  "Other",
];

const BUDGETS = [
  "Under $25,000",
  "$25,000 – $50,000",
  "$50,000 – $100,000",
  "$100,000 – $250,000",
  "$250,000+",
  "Prefer to discuss during consultation",
];

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  address: "",
  referralSource: "",
  preferredCommunication: "",
  projectType: "",
  projectTypeOther: "",
  improvementGoals: [],
  improvementGoalsOther: "",
  scope: [],
  planningResources: [],
  planningDetails: "",
  budget: "",
  finishLevel: "",
  desiredStartDate: "",
  targetCompletionDate: "",
  contractorPriorities: [],
  contractorPrioritiesOther: "",
  occupiedDuringConstruction: "",
  siteConcerns: [],
  siteConcernsOther: "",
  siteConcernDetails: "",
  description: "",
  inspiration: "",
  additionalInformation: "",
};

function readDraft() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    delete draft.images;
    return draft;
  } catch {
    return null;
  }
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function ProjectBriefLauncher({ className = "" }) {
  const [open, setOpen] = useState(false);

  return (
    <div className={`pb-launcher ${className}`}>
      <p className="pb-invite">
        Project Planner
      </p>
      <button
        type="button"
        className="pb-launch-btn"
        onClick={() => setOpen(true)}
      >
        Get Started Planning
      </button>

      <ProjectBriefModal isOpen={open} onClose={() => setOpen(false)} />

      <style>{`
        .pb-launcher {
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid rgba(141,189,189,0.3);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1.5rem;
        }
        .pb-invite {
          margin: 0;
          color: white;
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.65rem;
          font-weight: 300;
          line-height: 1.2;
        }
        .pb-launch-btn {
          background: var(--gold);
          color: white;
          border: none;
          border-radius: 4px;
          padding: 0.9rem 2rem;
          font-family: 'Barlow Condensed', sans-serif;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          cursor: pointer;
          transition: background 0.2s;
          white-space: nowrap;
        }
        .pb-launch-btn:hover {
          background: #a87340;
        }
        .pb-launch-btn:focus-visible {
          outline: 2px solid var(--gold);
          outline-offset: 3px;
        }
        @media (max-width: 560px) {
          .pb-launcher { align-items: stretch; flex-direction: column; }
          .pb-launch-btn { width: 100%; }
        }
      `}</style>
    </div>
  );
}

export default function ProjectBriefModal({ isOpen, onClose }) {
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState("idle"); // idle | saved | sending | sent | error
  const [submissionId, setSubmissionId] = useState("");
  const [uploadImages, setUploadImages] = useState([]);
  const [uploadStatus, setUploadStatus] = useState("idle"); // idle | uploading | uploaded | error
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);
  const dialogRef = useRef(null);

  const finishSubmission = useCallback(() => {
    setForm(emptyForm);
    setStatus("idle");
    setSubmissionId("");
    setUploadImages([]);
    setUploadStatus("idle");
    onClose();
  }, [onClose]);

  // Load any saved draft when the modal opens
  useEffect(() => {
    if (isOpen) {
      const draft = readDraft();
      if (draft) {
        setForm({ ...emptyForm, ...draft });
        setStatus("saved");
      }
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Auto-save draft on every change (debounced)
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      const isEmpty = JSON.stringify(form) === JSON.stringify(emptyForm);
      if (!isEmpty) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
        setStatus("saved");
      }
    }, 500);
    return () => clearTimeout(t);
  }, [form, isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (status === "sent") finishSubmission();
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [finishSubmission, isOpen, onClose, status]);

  if (!isOpen) return null;

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const toggleSelection = (field, value) =>
    setForm((f) => ({
      ...f,
      [field]: f[field].includes(value)
        ? f[field].filter((item) => item !== value)
        : [...f[field], value],
    }));

  async function handleFiles(fileList) {
    const files = Array.from(fileList).slice(
      0,
      MAX_IMAGES - uploadImages.length
    );
    const accepted = [];
    for (const file of files) {
      if (!["image/jpeg", "image/png"].includes(file.type)) continue;
      if (file.size > MAX_IMAGE_MB * 1024 * 1024) continue;
      const dataUrl = await fileToDataUrl(file);
      accepted.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        name: file.name,
        dataUrl,
        sizeKB: Math.round(file.size / 1024),
        file,
      });
    }
    setUploadImages((current) => [...current, ...accepted]);
    setUploadStatus("idle");
  }

  function removeImage(id) {
    setUploadImages((current) => current.filter((img) => img.id !== id));
    setUploadStatus("idle");
  }

  async function uploadProjectImages(id = submissionId) {
    if (!id || uploadImages.length === 0) return;
    setUploadStatus("uploading");

    try {
      for (let index = 0; index < uploadImages.length; index += 1) {
        const image = uploadImages[index];
        const payload = new FormData();
        payload.append("submissionId", id);
        payload.append("name", form.name);
        payload.append("email", form.email);
        payload.append("projectType", form.projectTypeOther || form.projectType);
        payload.append("imageNumber", String(index + 1));
        payload.append("notify", index === uploadImages.length - 1 ? "1" : "0");
        payload.append("images[]", image.file, image.name);

        const response = await fetch("/services/upload-client-remodeling-images.php", {
          method: "POST",
          body: payload,
        });
        const result = await response.json().catch(() => ({}));

        if (!response.ok || !result.success) {
          throw new Error(result.message || "Images could not be uploaded.");
        }
      }

      setUploadStatus("uploaded");
    } catch {
      setUploadStatus("error");
    }
  }

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) e.email = "Please enter an email address.";
    else if (!/^\S+@\S+\.\S+$/.test(form.email))
      e.email = "That email doesn't look right.";
    if (!form.projectType) e.projectType = "Please select a project type.";
    if (!form.description.trim())
      e.description = "Tell us a bit about what you have in mind.";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    setStatus("sending");
    try {
      const payload = new FormData();
      const arrayFields = [
        "improvementGoals",
        "scope",
        "planningResources",
        "contractorPriorities",
        "siteConcerns",
      ];

      Object.entries(form).forEach(([field, value]) => {
        if (arrayFields.includes(field)) {
          value.forEach((item) => payload.append(`${field}[]`, item));
          return;
        }
        payload.append(field, value);
      });

      const response = await fetch("/services/client-remodeling-questionnaire.php", {
        method: "POST",
        body: payload,
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Questionnaire could not be sent.");
      }

      setSubmissionId(result.submissionId);
      localStorage.removeItem(STORAGE_KEY);
      setStatus("sent");
      if (uploadImages.length > 0) {
        await uploadProjectImages(result.submissionId);
      }
    } catch {
      setStatus("error");
    }
  }

  function handleSaveForLater() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
    setStatus("saved");
  }

  function handleClearDraft() {
    localStorage.removeItem(STORAGE_KEY);
    setForm(emptyForm);
    setUploadImages([]);
    setUploadStatus("idle");
    setStatus("idle");
  }

  return createPortal(
    (
    <div
      className="pb-overlay"
      onMouseDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (status === "sent") finishSubmission();
        else onClose();
      }}
    >
      <div
        className="pb-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pb-title"
        ref={dialogRef}
      >
        {status === "sent" ? (
          <div className="pb-confirm">
            {uploadStatus === "uploaded" ? (
              <>
                <h2 id="pb-title">Photos received — thank you.</h2>
                <p>Your questionnaire and project photos have been sent successfully.</p>
                <button type="button" className="pb-btn pb-btn-primary" onClick={finishSubmission}>
                  Close
                </button>
              </>
            ) : uploadStatus === "uploading" ? (
              <>
                <h2 id="pb-title">Questionnaire received.</h2>
                <p>Your project details were sent successfully. Your photos are uploading now.</p>
                <button type="button" className="pb-btn pb-btn-primary" disabled>
                  Uploading photos…
                </button>
              </>
            ) : uploadStatus === "error" ? (
              <>
                <h2 id="pb-title">Questionnaire received.</h2>
                <p>
                  Your project details were sent, but the photos could not be uploaded.
                  Your selections are still here so you can retry.
                </p>
                <div className="pb-thumbs">
                  {uploadImages.map((img) => (
                    <div className="pb-thumb" key={img.id}>
                      <img src={img.dataUrl} alt={img.name} />
                    </div>
                  ))}
                </div>
                <div className="pb-confirm-actions">
                  <button type="button" className="pb-btn pb-btn-secondary" onClick={finishSubmission}>
                    Finish without photos
                  </button>
                  <button
                    type="button"
                    className="pb-btn pb-btn-primary"
                    onClick={() => uploadProjectImages()}
                  >
                    Retry photo upload
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 id="pb-title">Got it — thank you.</h2>
                <p>Your Client Remodeling Questionnaire was sent successfully.</p>
                <button type="button" className="pb-btn pb-btn-primary" onClick={finishSubmission}>
                  Close
                </button>
              </>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <div className="pb-header">
              <div>
                <h2 id="pb-title">Start Your Project</h2>
                <p className="pb-sub">
                  A few details and a couple of photos help us show up
                  ready to talk real numbers — not guesswork.
                </p>
              </div>
              <button
                type="button"
                className="pb-close"
                onClick={onClose}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="pb-body">
              {/* Contact info */}
              <fieldset className="pb-fieldset">
                <legend>Your information</legend>
                <div className="pb-grid-2">
                  <label className="pb-field">
                    <span>Name *</span>
                    <input
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      autoComplete="name"
                    />
                    {errors.name && <em className="pb-error">{errors.name}</em>}
                  </label>
                  <label className="pb-field">
                    <span>Email *</span>
                    <input
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      autoComplete="email"
                    />
                    {errors.email && <em className="pb-error">{errors.email}</em>}
                  </label>
                  <label className="pb-field">
                    <span>Phone</span>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      autoComplete="tel"
                    />
                  </label>
                  <label className="pb-field">
                    <span>Property address</span>
                    <input
                      type="text"
                      value={form.address}
                      onChange={update("address")}
                      autoComplete="street-address"
                    />
                  </label>
                  <label className="pb-field">
                    <span>How did you find us?</span>
                    <input
                      type="text"
                      value={form.referralSource}
                      onChange={update("referralSource")}
                    />
                  </label>
                  <label className="pb-field">
                    <span>Preferred communication</span>
                    <select
                      value={form.preferredCommunication}
                      onChange={update("preferredCommunication")}
                    >
                      <option value="">Select one</option>
                      {COMMUNICATION_METHODS.map((method) => (
                        <option key={method} value={method}>{method}</option>
                      ))}
                    </select>
                  </label>
                </div>
              </fieldset>

              {/* Project information */}
              <fieldset className="pb-fieldset">
                <legend>Project information</legend>
                <div className="pb-grid-2">
                  <label className="pb-field">
                    <span>Project type *</span>
                    <select value={form.projectType} onChange={update("projectType")}>
                      <option value="">Select one</option>
                      {PROJECT_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    {errors.projectType && (
                      <em className="pb-error">{errors.projectType}</em>
                    )}
                  </label>
                  {form.projectType === "Other" && (
                    <label className="pb-field">
                      <span>Other project type</span>
                      <input
                        type="text"
                        value={form.projectTypeOther}
                        onChange={update("projectTypeOther")}
                      />
                    </label>
                  )}
                </div>
                <label className="pb-field">
                  <span>Please describe your project goals *</span>
                  <textarea
                    rows={5}
                    placeholder="What are the main improvements you would like to achieve?"
                    value={form.description}
                    onChange={update("description")}
                  />
                  {errors.description && (
                    <em className="pb-error">{errors.description}</em>
                  )}
                </label>
                <div className="pb-field">
                  <span>Main improvement goals</span>
                  <div className="pb-check-grid">
                    {IMPROVEMENT_GOALS.map((goal) => (
                      <label className="pb-check" key={goal}>
                        <input
                          type="checkbox"
                          checked={form.improvementGoals.includes(goal)}
                          onChange={() => toggleSelection("improvementGoals", goal)}
                        />
                        <span>{goal}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {form.improvementGoals.includes("Other") && (
                  <label className="pb-field">
                    <span>Other improvement goal</span>
                    <input
                      type="text"
                      value={form.improvementGoalsOther}
                      onChange={update("improvementGoalsOther")}
                    />
                  </label>
                )}
              </fieldset>

              {/* Scope and planning */}
              <fieldset className="pb-fieldset">
                <legend>Scope &amp; planning</legend>
                <div className="pb-field">
                  <span>Are you considering?</span>
                  <div className="pb-check-grid">
                    {SCOPE_OPTIONS.map((option) => (
                      <label className="pb-check" key={option}>
                        <input
                          type="checkbox"
                          checked={form.scope.includes(option)}
                          onChange={() => toggleSelection("scope", option)}
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="pb-field">
                  <span>Do you currently have?</span>
                  <div className="pb-check-grid">
                    {PLANNING_RESOURCES.map((resource) => (
                      <label className="pb-check" key={resource}>
                        <input
                          type="checkbox"
                          checked={form.planningResources.includes(resource)}
                          onChange={() => toggleSelection("planningResources", resource)}
                        />
                        <span>{resource}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <label className="pb-field">
                  <span>If yes, please describe</span>
                  <textarea
                    rows={3}
                    value={form.planningDetails}
                    onChange={update("planningDetails")}
                  />
                </label>
                <label className="pb-field">
                  <span>Inspiration, links, or references (optional)</span>
                  <textarea
                    rows={3}
                    placeholder="Pinterest boards, a neighbor's patio you liked, a magazine clipping — anything that helps us see what you see."
                    value={form.inspiration}
                    onChange={update("inspiration")}
                  />
                </label>
              </fieldset>

              {/* Budget and timeline */}
              <fieldset className="pb-fieldset">
                <legend>Budget &amp; timeline</legend>
                <div className="pb-grid-2">
                  <label className="pb-field">
                    <span>Investment range</span>
                    <select value={form.budget} onChange={update("budget")}>
                      <option value="">Select one</option>
                      {BUDGETS.map((budget) => (
                        <option key={budget} value={budget}>{budget}</option>
                      ))}
                    </select>
                  </label>
                  <label className="pb-field">
                    <span>Expected finish level</span>
                    <select value={form.finishLevel} onChange={update("finishLevel")}>
                      <option value="">Select one</option>
                      {FINISH_LEVELS.map((level) => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                  </label>
                  <label className="pb-field">
                    <span>Ideal construction start</span>
                    <input
                      type="date"
                      value={form.desiredStartDate}
                      onChange={update("desiredStartDate")}
                    />
                  </label>
                  <label className="pb-field">
                    <span>Target completion date</span>
                    <input
                      type="date"
                      value={form.targetCompletionDate}
                      onChange={update("targetCompletionDate")}
                    />
                  </label>
                </div>
              </fieldset>

              {/* Decision process */}
              <fieldset className="pb-fieldset">
                <legend>Decision process</legend>
                <div className="pb-field">
                  <span>What is most important when selecting a contractor?</span>
                  <div className="pb-check-grid">
                    {CONTRACTOR_PRIORITIES.map((priority) => (
                      <label className="pb-check" key={priority}>
                        <input
                          type="checkbox"
                          checked={form.contractorPriorities.includes(priority)}
                          onChange={() => toggleSelection("contractorPriorities", priority)}
                        />
                        <span>{priority}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {form.contractorPriorities.includes("Other") && (
                  <label className="pb-field">
                    <span>Other priority</span>
                    <input
                      type="text"
                      value={form.contractorPrioritiesOther}
                      onChange={update("contractorPrioritiesOther")}
                    />
                  </label>
                )}
              </fieldset>

              {/* Property and logistics */}
              <fieldset className="pb-fieldset">
                <legend>Property &amp; logistics</legend>
                <label className="pb-field">
                  <span>Will the home be occupied during construction?</span>
                  <select
                    value={form.occupiedDuringConstruction}
                    onChange={update("occupiedDuringConstruction")}
                  >
                    <option value="">Select one</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </label>
                <div className="pb-field">
                  <span>Site restrictions or concerns</span>
                  <div className="pb-check-grid">
                    {SITE_CONCERNS.map((concern) => (
                      <label className="pb-check" key={concern}>
                        <input
                          type="checkbox"
                          checked={form.siteConcerns.includes(concern)}
                          onChange={() => toggleSelection("siteConcerns", concern)}
                        />
                        <span>{concern}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {form.siteConcerns.includes("Other") && (
                  <label className="pb-field">
                    <span>Other site concern</span>
                    <input
                      type="text"
                      value={form.siteConcernsOther}
                      onChange={update("siteConcernsOther")}
                    />
                  </label>
                )}
                <label className="pb-field">
                  <span>Please explain if needed</span>
                  <textarea
                    rows={3}
                    value={form.siteConcernDetails}
                    onChange={update("siteConcernDetails")}
                  />
                </label>
              </fieldset>

              {/* Additional information */}
              <fieldset className="pb-fieldset">
                <legend>Additional information</legend>
                <label className="pb-field">
                  <span>Anything else you would like us to know?</span>
                  <textarea
                    rows={4}
                    value={form.additionalInformation}
                    onChange={update("additionalInformation")}
                  />
                </label>
              </fieldset>

              {/* Project photos */}
              <fieldset className="pb-fieldset">
                <legend>Project photos</legend>
                <p className="pb-hint">
                  Optional: add up to {MAX_IMAGES} JPG or PNG photos of the space.
                  Each image must be {MAX_IMAGE_MB} MB or smaller.
                </p>
                <div
                  className="pb-dropzone"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    handleFiles(e.dataTransfer.files);
                  }}
                >
                  <span>Click to select or drag photos here</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png"
                    multiple
                    hidden
                    onChange={(e) => handleFiles(e.target.files)}
                  />
                </div>

                {uploadImages.length > 0 && (
                  <div className="pb-thumbs">
                    {uploadImages.map((img) => (
                      <div className="pb-thumb" key={img.id}>
                        <img src={img.dataUrl} alt={img.name} />
                        <button
                          type="button"
                          className="pb-thumb-remove"
                          onClick={() => removeImage(img.id)}
                          aria-label={`Remove ${img.name}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </fieldset>

            </div>

            <div className="pb-footer">
              <span className="pb-status">
                {status === "saved" && "Draft saved on this device"}
                {status === "sending" && "Sending…"}
                {status === "error" && "Something went wrong — please try again."}
              </span>
              <span className="pb-terms">
                By submitting, you acknowledge our <Link to="/terms-and-privacy" onClick={onClose}>Terms &amp; Privacy Notice</Link>.
              </span>
              <div className="pb-footer-actions">
                <button
                  type="button"
                  className="pb-btn pb-btn-ghost"
                  onClick={handleClearDraft}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="pb-btn pb-btn-secondary"
                  onClick={handleSaveForLater}
                >
                  Save &amp; finish later
                </button>
                <button
                  type="submit"
                  className="pb-btn pb-btn-primary"
                  disabled={status === "sending"}
                >
                  {status === "sending" ? "Sending…" : "Send my project file"}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>

      <style>{`
        .pb-overlay {
          position: fixed; inset: 0;
          background: rgba(26,42,46,0.78);
          display: flex; align-items: center; justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }
        .pb-dialog {
          background: var(--off-white);
          width: 100%;
          max-width: 680px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          border: 1px solid rgba(141,189,189,0.4);
          box-shadow: 0 20px 60px rgba(0,0,0,0.35);
        }
        .pb-dialog > form {
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        .pb-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 1.75rem 2rem 1.5rem;
          background: var(--aqua-dark);
          border-bottom: 2px solid var(--gold);
        }
        .pb-header h2 {
          margin: 0 0 0.35rem;
          color: white;
          font-family: 'Cormorant Garamond', serif;
          font-size: 2rem;
          font-weight: 300;
        }
        .pb-sub { margin: 0; color: rgba(255,255,255,0.65); font-size: 0.9rem; font-weight: 300; max-width: 36rem; }
        .pb-close {
          background: none; border: none; font-size: 1.6rem; line-height: 1;
          cursor: pointer; color: var(--aqua-light); padding: 0 0.25rem;
        }
        .pb-close:hover { color: white; }
        .pb-body {
          overflow-y: auto;
          padding: 1.5rem 2rem;
          flex: 1;
          min-height: 0;
        }
        .pb-fieldset {
          border: none;
          padding: 0;
          margin: 0 0 1.5rem;
        }
        .pb-fieldset legend {
          font-family: 'Barlow Condensed', sans-serif;
          font-size: 0.68rem;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          color: var(--gold);
          font-weight: 700;
          margin-bottom: 0.75rem;
          padding: 0;
        }
        .pb-grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.9rem;
        }
        .pb-span-2 { grid-column: 1 / -1; }
        .pb-field {
          display: flex;
          flex-direction: column;
          gap: 0.3rem;
          font-size: 0.85rem;
          color: var(--dark);
          margin-bottom: 0.9rem;
        }
        .pb-field span {
          font-family: 'Barlow Condensed', sans-serif;
          font-size: 0.68rem;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--mid);
        }
        .pb-field input,
        .pb-field select,
        .pb-field textarea {
          border: 1px solid rgba(55,100,123,0.28);
          border-radius: 4px;
          padding: 0.75rem 1rem;
          font-size: 0.92rem;
          font-family: 'Barlow', sans-serif;
          font-weight: 300;
          color: var(--dark);
          background: white;
        }
        .pb-field input:focus,
        .pb-field select:focus,
        .pb-field textarea:focus {
          outline: none;
          border-color: var(--gold);
        }
        .pb-check-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 0.55rem 1rem;
          padding: 0.4rem 0;
        }
        .pb-check {
          display: flex;
          align-items: flex-start;
          gap: 0.55rem;
          cursor: pointer;
        }
        .pb-field .pb-check input {
          width: 1rem;
          height: 1rem;
          margin: 0.05rem 0 0;
          padding: 0;
          accent-color: var(--gold);
          flex: 0 0 auto;
        }
        .pb-field .pb-check span {
          font-family: 'Barlow', sans-serif;
          font-size: 0.85rem;
          font-weight: 300;
          letter-spacing: normal;
          line-height: 1.35;
          text-transform: none;
          color: var(--dark);
        }
        .pb-hint { font-size: 0.85rem; font-weight: 300; line-height: 1.6; color: var(--mid); margin: 0 0 0.75rem; }
        .pb-error { color: #b3261e; font-size: 0.78rem; font-style: normal; }
        .pb-dropzone {
          border: 1px dashed rgba(55,100,123,0.45);
          background: white;
          padding: 1.5rem;
          text-align: center;
          color: var(--mid);
          font-size: 0.9rem;
          font-weight: 300;
          cursor: pointer;
          transition: border-color 0.15s ease, background 0.15s ease;
        }
        .pb-dropzone:hover { border-color: var(--gold); background: #fff; }
        .pb-thumbs {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
          gap: 0.6rem;
          margin-top: 0.9rem;
        }
        .pb-thumb {
          position: relative;
          aspect-ratio: 1;
          overflow: hidden;
          border: 1px solid rgba(55,100,123,0.25);
        }
        .pb-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .pb-thumb-remove {
          position: absolute; top: 2px; right: 2px;
          background: rgba(0,0,0,0.65); color: #fff;
          border: none; border-radius: 50%;
          width: 20px; height: 20px;
          font-size: 0.9rem; line-height: 1; cursor: pointer;
        }
        .pb-footer {
          border-top: 1px solid rgba(55,100,123,0.2);
          padding: 1rem 2rem;
          background: white;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
        }
        .pb-status { font-size: 0.8rem; font-weight: 300; color: var(--mid); }
        .pb-terms { width: 100%; font-size: 0.72rem; font-weight: 300; color: var(--mid); }
        .pb-terms a { color: var(--aqua-dark); }
        .pb-footer-actions { display: flex; gap: 0.6rem; margin-left: auto; }
        .pb-btn {
          border-radius: 4px;
          padding: 0.75rem 1.1rem;
          font-family: 'Barlow Condensed', sans-serif;
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          cursor: pointer;
          border: 1px solid transparent;
        }
        .pb-btn-primary { background: var(--gold); color: white; }
        .pb-btn-primary:hover { background: #a87340; }
        .pb-btn-primary:disabled { opacity: 0.6; cursor: default; }
        .pb-btn-secondary { background: white; color: var(--aqua-dark); border-color: rgba(55,100,123,0.4); }
        .pb-btn-secondary:hover { border-color: var(--aqua-dark); }
        .pb-btn-ghost { background: none; color: var(--mid); border-color: transparent; }
        .pb-btn-ghost:hover { color: #b3261e; }
        .pb-confirm { padding: 3rem 2rem; text-align: center; overflow-y: auto; }
        .pb-confirm h2 { margin-bottom: 0.5rem; font-family: 'Cormorant Garamond', serif; font-size: 2rem; font-weight: 300; color: var(--dark); }
        .pb-confirm p { color: var(--mid); font-weight: 300; margin-bottom: 1.5rem; }
        .pb-confirm .pb-thumbs { text-align: left; }
        .pb-confirm-actions { display: flex; justify-content: center; gap: 0.75rem; margin-top: 1.5rem; }
        .pb-confirm-actions .pb-btn:disabled { opacity: 0.6; cursor: default; }

        @media (max-width: 560px) {
          .pb-grid-2 { grid-template-columns: 1fr; }
          .pb-check-grid { grid-template-columns: 1fr; }
          .pb-confirm-actions { flex-direction: column; }
          .pb-footer { flex-direction: column; align-items: stretch; }
          .pb-footer-actions { margin-left: 0; flex-direction: column; }
        }
      `}</style>
    </div>
    ),
    document.body
  );
}
